import React, { FC, useState, useCallback } from "react";
import {
  Icon,
  Card,
  Button,
  SheetContainer,
  SheetContainerProps,
  Text,
  Spinner,
  Stack,
  CollapsedText
} from "@bookingcom/bui-react";
import { SparklesIcon } from "@bookingcom/bui-assets-react/streamline";
import { t } from "@bookingcom/lingojs-core";
import { useI18n } from "@bookingcom/lingojs-react";

import styles from "./GenAiSearchSummary.module.css";

type Props = {
  summaryText: string;
  variant?: "card" | "collapsed_text" | "modal_only";
  className?: string;
  summaryVisible?: boolean;
  modalPosition?: SheetContainerProps["position"];
  isLoading?: boolean;
  isError?: boolean;
  onCtaClick?: VoidFunction;
  onModalClose?: VoidFunction;
};

const GenAiSearchSummary: FC<Props> = ({
  variant = "card",
  className,
  summaryVisible, // if passed, summary visibility is controlled only by a parent component (to be used with variant="modal_only")
  modalPosition = "center",
  summaryText,
  isLoading,
  isError,
  onCtaClick,
  onModalClose
}) => {
  const i18n = useI18n();
  const [summaryVisibleInternal, setSummaryVisibleInternal] = useState(false);
  const isSummaryVisible = summaryVisible === undefined ? summaryVisibleInternal : summaryVisible;

  const ctaClickHandler = useCallback(() => {
    setSummaryVisibleInternal(true);
    onCtaClick?.();
  }, [onCtaClick]);

  return (
    <>
      {variant === "card" || variant === "collapsed_text" ? (
        <div className={className}>
          {variant === "card" && <GenAiCard onCtaClick={ctaClickHandler} />}

          {variant === "collapsed_text" && (
            <GenAiCollapsedText
              summaryText={summaryText}
              isLoading={isLoading}
              isError={isError}
              onCollapsedTextClick={ctaClickHandler}
            />
          )}
        </div>
      ) : null}

      {variant === "card" || variant === "modal_only" ? (
        <SheetContainer
          active={isSummaryVisible}
          position={modalPosition}
          title={i18n.trans(t("flights_sr_genai_modal_search_summary_heading"))}
          onCloseTrigger={() => {
            setSummaryVisibleInternal(false);
            onModalClose?.();
          }}
          closeAriaLabel={i18n.trans(t("a11y_flights_sr_genai_summary_close"))}
        >
          <div className={styles.modalContent}>
            <ModalContent text={summaryText} isLoading={isLoading} isError={isError} />
          </div>
        </SheetContainer>
      ) : null}
    </>
  );
};

const GenAiCard: FC<{
  onCtaClick?: VoidFunction;
}> = ({ onCtaClick }) => {
  const i18n = useI18n();

  return (
    <Card>
      <Text variant="strong_1" tagName="h2" className={styles.cardTitle}>
        {i18n.trans(t("flights_sr_genai_search_summary_title"))}
      </Text>

      <Text variant="body_2" className={styles.cardSubtitle}>
        {i18n.trans(t("flights_sr_genai_search_summary_subtitle_stops_prices"))}
      </Text>

      <Button variant="secondary" icon={SparklesIcon} wide={true} onClick={onCtaClick}>
        {i18n.trans(t("flights_sr_genai_search_summary_cta"))}
      </Button>
    </Card>
  );
};

const GenAiCollapsedText: FC<{
  summaryText: string;
  isLoading?: boolean;
  isError?: boolean;
  onCollapsedTextClick?: VoidFunction;
}> = ({ summaryText, isLoading, isError, onCollapsedTextClick }) => {
  const i18n = useI18n();
  let content;

  if (isLoading) {
    content = i18n.trans(t("flights_sr_genai_summary_loading_creating_automated"));
  } else if (isError) {
    content = i18n.trans(t("flights_sr_genai_summary_error_couldnt_load"));
  } else {
    content = summaryText;
  }

  return (
    <Stack>
      <Stack.Item>
        <Stack direction="row" alignItems="center" gap={2}>
          {isLoading ? <Spinner size="medium" /> : <Icon svg={SparklesIcon} size="medium" color="action" />}

          <Text variant="strong_1" tagName="h2">
            {i18n.trans(t("flights_sr_genai_search_summary_title"))}
          </Text>
        </Stack>
      </Stack.Item>
      <Stack.Item>
        <CollapsedText
          text={content}
          readMoreLabel={i18n.trans(t("flights_sr_genai_summary_view_more"))}
          readLessLabel={i18n.trans(t("flights_sr_genai_summary_view_less"))}
          attributes={{
            onClick: () => {
              onCollapsedTextClick?.();
            }
          }}
        />
      </Stack.Item>
    </Stack>
  );
};

const ModalContent: FC<{ text: string; isLoading?: boolean; isError?: boolean }> = ({ text, isLoading, isError }) => {
  const i18n = useI18n();
  let content;

  if (isLoading) {
    content = (
      <Stack direction="row" alignItems="center" gap={2}>
        <Spinner size="small" />
        <Text variant="body_2">{i18n.trans(t("flights_sr_genai_search_summary_loading"))}</Text>
      </Stack>
    );
  } else if (isError) {
    content = <Text variant="body_2">{i18n.trans(t("flights_sr_genai_modal_search_summary_loading_error"))}</Text>;
  } else {
    // This is temporary workaround, and must be revisited after GenAI search summary MVP.
    // We should find a proper way to template this string from ML - an alternative approach is to be discussed by Web funnel team.
    const textPieces = text.split(/{start_bold}|{end_bold}/);

    content = (
      <div>
        {textPieces.map((piece, index) => {
          const isItBoldPiece = index % 2 === 1;
          return isItBoldPiece ? <strong>{piece}</strong> : piece;
        })}
      </div>
    );
  }

  return content;
};

export default GenAiSearchSummary;
